import React from 'react';

export interface ReviewDimension {
  label: string;
  avg: number; // 1–5
  icon: string;
}

interface PerformanceMetricsProps {
  dimensions: ReviewDimension[];
  reviewCount: number;
  isMobile?: boolean;
  isDesktop?: boolean;
}

function pct(avg: number): number {
  return Math.round((avg / 5) * 100);
}

function avgLabel(avg: number): string {
  return avg.toFixed(1);
}

const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
  dimensions,
  reviewCount,
  isMobile,
  isDesktop,
}) => {
  if (reviewCount === 0) return null;

  if (isMobile) {
    return (
      <section className="px-container-margin mt-stack-xl space-y-stack-lg md:hidden">
        <h3 className="font-headline-sm text-headline-sm text-primary mb-stack-md">Performance Metrics</h3>
        <div className="space-y-4">
          {dimensions.map((dim) => (
            <div key={dim.label} className="space-y-1">
              <div className="flex justify-between text-label-bold font-label-bold text-on-surface">
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px] text-primary">{dim.icon}</span>
                  {dim.label}
                </span>
                <span>{avgLabel(dim.avg)} / 5</span>
              </div>
              <div className="w-full bg-surface-container-high h-2 rounded-full">
                <div
                  className="bg-secondary-container h-2 rounded-full transition-all duration-500"
                  style={{ width: `${pct(dim.avg)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-on-surface-variant">Based on {reviewCount} review{reviewCount !== 1 ? 's' : ''}</p>
      </section>
    );
  }

  if (isDesktop) {
    return (
      <section className="bg-surface-container-lowest rounded-xl shadow-sm p-stack-lg border border-outline-variant space-y-stack-lg hidden md:block">
        <div className="flex items-center justify-between">
          <h3 className="font-headline-sm text-headline-sm text-primary">Performance Metrics</h3>
          <span className="text-xs text-on-surface-variant">
            {reviewCount} review{reviewCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-gutter">
          {dimensions.map((dim) => (
            <div
              key={dim.label}
              className="p-stack-lg bg-surface rounded-lg border border-outline-variant/30 text-center space-y-stack-sm"
            >
              <div className="flex justify-center mb-2">
                <div className="w-16 h-16 rounded-full border-4 border-secondary flex items-center justify-center">
                  <span className="font-headline-sm text-secondary">{avgLabel(dim.avg)}</span>
                </div>
              </div>
              <p className="font-label-bold text-sm">{dim.label}</p>
              <div className="w-full bg-surface-container-high h-1.5 rounded-full">
                <div
                  className="bg-secondary h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${pct(dim.avg)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return null;
};

export default PerformanceMetrics;
